// In-round combo — pure state machine (practice-loop-v2).
//
// Grace principle guardrail: this module is celebration bookkeeping only.
// It must never touch XP, unlock, SRS, or the daily streak — nothing here
// is sent to the server.

import type { CompanionId } from '@skena/schema'

export interface ComboState {
  /** Consecutive correct answers this round. */
  combo: number
  /** Sparrow passive: has the once-per-round shield been consumed? */
  shieldUsed: boolean
}

export const INITIAL_COMBO: ComboState = { combo: 0, shieldUsed: false }

export type ComboEvent =
  | 'advance' // correct answer, combo grew
  | 'shielded' // sparrow absorbed the first miss — combo kept
  | 'reset' // wrong answer, combo back to 0 (silently)

export function nextComboState(
  prev: ComboState,
  args: { isCorrect: boolean; companionId: CompanionId | null },
): { state: ComboState; event: ComboEvent } {
  if (args.isCorrect) {
    return { state: { ...prev, combo: prev.combo + 1 }, event: 'advance' }
  }
  if (args.companionId === 'sparrow' && !prev.shieldUsed) {
    return { state: { ...prev, shieldUsed: true }, event: 'shielded' }
  }
  return { state: { ...prev, combo: 0 }, event: 'reset' }
}

/**
 * Celebration tier fired by reaching this combo value: 1 at 3, 2 at 5,
 * 3 at 7 and every correct answer beyond (spec: 8+ reuses tier-7).
 * 0 = no tier celebration (the normal correct feedback still plays).
 */
export function tierForCombo(combo: number): 0 | 1 | 2 | 3 {
  if (combo >= 7) return 3
  if (combo === 5) return 2
  if (combo === 3) return 1
  return 0
}
